import {
  type ChartAxisVisibleRange,
  ChartContainer,
  ChartHeader,
  ChartLegend,
  type ChartPoint,
  LineChart,
} from "@assetwatch/charts";
import { Alert } from "@assetwatch/ui";
import { getRouteApi } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { CmeAsset } from "./api";
import { errorSubtext } from "./errorSubtext";
import {
  axisSeriesLabel,
  chartFeatureStems,
  combineQueryCosts,
  DEFAULT_HISTORY,
  detectedAxisIds,
  detectedFeatureId,
  featureLabel,
  featureSummary,
  featureTrendShading,
  formatAxisMagnitude,
  formatAxisTimestamp,
  formatChartValue,
  formatRpm,
  historyRange,
  historyViewport,
  minimumTrendDays,
  parseViewport,
  predicted1xHz,
  RPM_FEATURE,
  rpmByTimestamp,
  rpmForHoveredValue,
  seriesPoints,
  stemSeriesFeatures,
} from "./lib";
import {
  useCmeFeatureDescriptions,
  useCmeFeatureSeries,
  useCmeFeatureSeriesSet,
} from "./queries";

const routeApi = getRouteApi("/_app/cme-feedback");

/**
 * The selected feature's history for one monitoring point: one query per axis
 * over the last 3 months. The page calls this once and hands the result
 * to the graph and to the details column (for the scan cost), so both read the
 * same queries. `featureId` falls back to the point's first feature.
 */
export function useTrendSeries(
  asset: CmeAsset,
  featureId: string | undefined,
  windowEnd: string,
) {
  // Always 3 months: enough history for a slow trend, and a fixed, predictable
  // scan. A `history` value in an old link is ignored.
  const range = historyRange(DEFAULT_HISTORY);
  const stems = chartFeatureStems(asset);
  const stem = stems.find((item) => item.id === featureId) ?? stems[0];
  const axes = stem ? stemSeriesFeatures(stem) : [];
  const queries = useCmeFeatureSeriesSet(
    asset,
    axes,
    axes.map((axis) => detectedFeatureId(axis)),
    windowEnd,
    range.days,
  );
  // RPM over the same window, for the tooltip and the predicted 1X. It's a
  // separate partition (~$0.01 for 3 months), fetched alongside the feature
  // and never holding the graph back.
  const rpmQuery = useCmeFeatureSeries(
    {
      id: asset.id,
      monitoring_point_id: asset.monitoring_point_id,
      ...RPM_FEATURE,
      end_date: windowEnd,
    },
    range.days,
  );
  const rpm = rpmByTimestamp(rpmQuery.data);
  const pending = queries.some((query) => query.isPending);
  const loaded = [...queries, rpmQuery].flatMap((query) =>
    query.data ? [query.data.cost] : [],
  );
  return {
    range,
    stem,
    axes,
    queries,
    pending,
    failed: queries.find((query) => query.isError),
    rpm,
    /** Null while RPM loads or when there were no readings in the week. */
    predicted1x: rpmQuery.isPending ? null : predicted1xHz(rpm, windowEnd),
    rpmPending: rpmQuery.isPending,
    cost: pending || rpmQuery.isPending ? null : combineQueryCosts(loaded),
  };
}

export type TrendSeries = ReturnType<typeof useTrendSeries>;

/**
 * The graph for the selected feature, full width under the feedback pane. The
 * zoomed chart window is URL state.
 */
export function TrendChart({
  asset,
  series,
}: {
  asset: CmeAsset;
  series: TrendSeries;
}) {
  const search = routeApi.useSearch();
  const { range, stem, axes, queries, pending, failed } = series;
  // Same cached request as the feature list; the summary heads the subtitle.
  const descriptions = useCmeFeatureDescriptions(true);
  const [axisPick, setAxisPick] = useState<{
    key: string;
    ids: string[];
  } | null>(null);

  const axisKey = stem ? `${asset.id}:${stem.id}` : "";
  const visibleAxisIds = !stem
    ? []
    : axisPick?.key === axisKey
      ? axisPick.ids
      : detectedAxisIds(asset, stem);

  // Always X, Y, Z in that order, so each axis keeps the same palette color.
  const chartSeries = axes.map((axis, index) => {
    const query = queries[index];
    return {
      id: axis.axis ?? axis.name,
      label: axis.axis
        ? axisSeriesLabel(axis.axis)
        : featureLabel(stem?.stem ?? axis.name),
      data: query?.data ? seriesPoints(query.data) : [],
      valueUnit: query?.data?.y_units || undefined,
    };
  });
  const firstVisibleId = chartSeries.find((item) =>
    visibleAxisIds.includes(item.id),
  )?.id;

  // The chart holds on to what it was first given: the tooltip formatter (it
  // refreshes its config only when its own update keys change) and the series
  // its tooltip reads. So the formatter reads everything it needs from this
  // ref, which always has the current points, first visible axis and RPM.
  const tooltipState = useRef({
    points: new Map<string, ChartPoint[]>(),
    firstVisibleId: undefined as string | undefined,
    rpm: series.rpm,
  });
  useEffect(() => {
    tooltipState.current = {
      points: new Map(chartSeries.map((item) => [item.id, item.data])),
      firstVisibleId,
      rpm: series.rpm,
    };
  }, [chartSeries, firstVisibleId, series.rpm]);

  if (!stem) {
    return (
      <Alert
        subtext="This monitoring point has no severity feature to plot."
        title="No feature trend"
        tone="warning"
      />
    );
  }
  const yUnit = chartSeries.find((item) => item.valueUnit)?.valueUnit;
  const yAxisTitle = yUnit ? `Value (${yUnit})` : "Value";
  // The chart picks a date axis only when it is built, from the points it has
  // then. Remount once data arrives so the hover shows a date, not Unix seconds.
  const hasData = chartSeries.some((item) => item.data.length > 0);
  const lastTimestamp = queries
    .flatMap((query) => query.data?.timestamps.at(-1) ?? [])
    .reduce<number | undefined>(
      (latest, timestamp) =>
        latest === undefined || timestamp > latest ? timestamp : latest,
      undefined,
    );
  const viewport =
    (lastTimestamp !== undefined
      ? historyViewport(range.id, lastTimestamp)
      : undefined) ?? parseViewport(search.x_start, search.x_stop);
  const visibleRange: ChartAxisVisibleRange | undefined = viewport
    ? {
        min: new Date(viewport.min * 1000),
        max: new Date(viewport.max * 1000),
      }
    : undefined;

  return (
    // Gray like the panels above it, on the page's white canvas.
    <ChartContainer className="bg-surface-low">
      <div className="flex min-w-0 flex-col gap-(--spacing-8)">
        <ChartHeader
          subtitle={[
            featureSummary(descriptions.data, stem.stem),
            "last 3 months",
          ]
            .filter(Boolean)
            .join(" · ")}
          title={`${asset.monitoring_point || "Unknown point"} · ${featureLabel(stem.stem)}`}
        />
        {failed && !hasData ? (
          <Alert
            subtext={errorSubtext(failed.error)}
            title={`Couldn't load ${featureLabel(stem.stem)}`}
            tone="negative"
          />
        ) : null}
        <div className="flex min-w-0 flex-col items-center gap-(--spacing-8)">
          <div className="min-w-0 w-full">
            <LineChart
              // Remount per point and feature: the chart keeps its first series and
              // tooltip formatter, so switching cards must give it fresh ones.
              key={`${asset.id}:${stem.id}:${range.id}:${hasData ? "data" : "empty"}`}
              emptyMessage={
                pending
                  ? `Loading ${featureLabel(stem.stem)}…`
                  : "No readings for this feature in this range."
              }
              // Sized so the feedback pane and the graph fit one screen.
              height={300}
              series={chartSeries}
              shadingRegions={featureTrendShading(
                stem.id,
                asset.detected_date,
                minimumTrendDays(asset.feature_trend_days, stem.stem),
              )}
              tooltip={{
                // RPM rides on the first visible axis row only, so X, Y and Z
                // don't each repeat it. No extra series or axis on the chart.
                formatValue: (value, item) => {
                  const text = formatChartValue(value, item.valueUnit);
                  const current = tooltipState.current;
                  if (item.id !== current.firstVisibleId) {
                    return text;
                  }
                  const hz = rpmForHoveredValue(
                    value,
                    current.points.get(item.id) ?? [],
                    current.rpm,
                  );
                  return hz === null ? text : `${text} · RPM ${formatRpm(hz)}`;
                },
              }}
              visibleSeriesIds={visibleAxisIds}
              xAxis={{
                formatTick: (value) => formatAxisTimestamp(value, range.id),
                title: "Timestamp",
                visibleRange,
              }}
              yAxis={{
                formatTick: (value) =>
                  formatAxisMagnitude(
                    typeof value === "number" ? value : Number.NaN,
                  ),
                title: yAxisTitle,
              }}
              zoom={{ pan: true, select: true }}
            />
          </div>
          <ChartLegend
            onToggleSeries={(seriesId) => {
              const isVisible = visibleAxisIds.includes(seriesId);
              const next = isVisible
                ? visibleAxisIds.length === 1
                  ? visibleAxisIds
                  : visibleAxisIds.filter((id) => id !== seriesId)
                : [...visibleAxisIds, seriesId];
              setAxisPick({ key: axisKey, ids: next });
            }}
            series={chartSeries}
            visibleSeriesIds={visibleAxisIds}
          />
        </div>
      </div>
    </ChartContainer>
  );
}
